import Image from "next/image";
import type { ResolvedBrandTheme } from "@/lib/branding/brand-theme";
import { cn } from "@/lib/utils";

const HEIGHT = { sm: 20, md: 28, lg: 36 } as const;

/**
 * A brand's mark inside a branded block: its logo when it has one, otherwise
 * its name set as a wordmark in the brand colour. Sized by height so a wide
 * logo and a square one sit on the same line; the box is reserved from the
 * stored aspect ratio, so nothing shifts when the bytes arrive.
 */
export function BrandMark({
  brand,
  name,
  size = "md",
  className,
}: {
  brand: ResolvedBrandTheme;
  name: string;
  size?: keyof typeof HEIGHT;
  className?: string;
}) {
  const height = HEIGHT[size];
  if (brand.logo) {
    const width = Math.round(
      height * Math.min(Math.max(brand.logo.aspectRatio, 0.5), 5),
    );
    return (
      <Image
        src={brand.logo.url}
        alt={name}
        width={width}
        height={height}
        style={{ width, height }}
        className={cn("shrink-0 object-contain", className)}
      />
    );
  }
  return (
    <span
      className={cn(
        "block truncate leading-none font-black tracking-[0.06em] uppercase",
        size === "lg" ? "text-[26px]" : size === "md" ? "text-xl" : "text-sm",
        className,
      )}
      style={{ color: "var(--brand-accent-text)" }}
    >
      {name}
    </span>
  );
}
